import { Field } from '@flywire/headlessui-tailwindcss';
import { useDatePicker } from '@flywire/react-headlessui';
import type { InputHTMLAttributes } from 'react';
import { useTranslations } from 'utils/translations/useTranslations';

import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden';

const DATE_PATTERN = 'd{1,2}/d{1,2}/d{4}';

type DatePickerProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'onBlur'
> & {
  dateFormat?: string;
  datePattern?: string;
  error?: string | boolean;
  hint?: string;
  label?: string;
  locale?: string;
  readOnly?: boolean;
  onChange?: (name: string, value?: string) => void;
  onBlur?: (name: string) => void;
};

const DatePicker = ({
  id = 'datePicker',
  name = 'datePicker',
  dateFormat,
  datePattern = DATE_PATTERN,
  disabled = false,
  label,
  readOnly = false,
  error,
  hint,
  onChange,
  onBlur,
  value,
  ...props
}: DatePickerProps) => {
  const datePickerProps = useDatePicker({
    dateFormat,
    name,
    value,
    onBlur,
    onChange,
  });

  const i18n = useTranslations();

  return (
    <Field floatingLabel={true}>
      <div className="relative">
        <Field.Input
          {...props}
          id={id}
          name={name}
          type="date"
          role="textbox"
          pattern={datePattern}
          disabled={disabled}
          readOnly={readOnly && !error}
          data-testid={`${id}-input`}
          placeholder={label}
          {...datePickerProps}
        />
        {label && (
          <Field.Label
            id={`${id}-label`}
            htmlFor={id}
            className="peer-focus:z-10"
          >
            {label}
          </Field.Label>
        )}
      </div>
      {hint && (
        <Field.Hint data-testid={`${name}-hint`} className="block mb-0">
          {hint}
        </Field.Hint>
      )}
      {error && typeof error === 'string' && (
        <Field.Error data-testid={`${name}-error`} className="block mb-0">
          <VisuallyHidden as="span">{label}</VisuallyHidden>
          {i18n.t(error)}
        </Field.Error>
      )}
    </Field>
  );
};

export { DatePicker };
